import React from "react";
import moment from "moment";
import {Box, Button, Grid, Typography} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {canEditAdditionalWork, canDeleteAdditionalWork} from "../../../../../utils/additionalWorkPermissions";

const strTrim = (v) => (v == null ? "" : String(v).trim());
const hasStr = (v) => strTrim(v) !== "";

const formatCostRub = (cost) => {
	const s = strTrim(cost);
	if (!s) {
		return "";
	}
	const n = parseFloat(s.replace(",", "."));
	if (Number.isNaN(n)) {
		return `${s} ₽`;
	}
	return `${n.toLocaleString("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₽`;
};

const mechanicDisplayName = (mechanic) => {
	if (!mechanic) {
		return "";
	}
	return [mechanic.firstName, mechanic.lastName].filter(Boolean).join(" ").trim();
};

/** Как в DialogAdditionalWork: полный url с бэка или статика по имени файла */
const pictureUrl = (pic) => {
	if (pic?.url) {
		return pic.url;
	}
	if (pic?.name) {
		return [process.env.REACT_APP_HOST_API, "/static/service/", pic.name].join("");
	}
	return "";
};

const AdditionalWorksList = ({
	items,
	showAddButton,
	onAddAdditionalWork,
	onEditAdditionalWork,
	onDeleteAdditionalWork,
	onOpenDialogImagesVideos,
	userRole,
	currentUserId,
}) => {
	const classes = useStyles();
	const list = items || [];

	return (
		<>
			<Grid container alignItems="center" justifyContent="space-between" className={classes.sectionHeader}>
				<Grid item>
					<Typography variant="h3" textAlign="left" className={classes.sectionTitle}>Доп работы</Typography>
				</Grid>
				{Boolean(showAddButton && onAddAdditionalWork) && (
					<Grid item>
						<Button variant="contained" className={classes.headerButton} onClick={onAddAdditionalWork}>
							Добавить доп. работу
						</Button>
					</Grid>
				)}
			</Grid>
			<Box className={classes.list}>
				{list.map((item) => {
					const hasQty = hasStr(item.quantity);
					const hasUnit = hasStr(item.unit);
					const hasQtyOrUnit = hasQty || hasUnit;
					const hasCost = hasStr(item.cost);
					const mechanicName = mechanicDisplayName(item.mechanic);
					const hasMechanic = Boolean(mechanicName);
					const createdLabel = item.createdAt
						? moment(item.createdAt).format("DD.MM.YYYY HH:mm")
						: "";
					const hasPrimaryLine = hasQtyOrUnit || hasCost;
					const pictures = Array.isArray(item.pictures) ? item.pictures : [];
					const pictureItems = pictures
						.map((pic) => ({pic, src: pictureUrl(pic)}))
						.filter(({src}) => Boolean(src));
					const hasPictures = pictureItems.length > 0;
					/** Формат как у рекомендаций / DialogImagesVideos: `fileUrl` для превью в галерее */
					const galleryFiles = pictureItems.map(({pic, src}) => ({...pic, fileUrl: src}));
					const hasBodyContent = hasPrimaryLine || hasMechanic || Boolean(createdLabel);

					const canEdit = Boolean(onEditAdditionalWork && canEditAdditionalWork(userRole, item, currentUserId));
					const canDelete = Boolean(onDeleteAdditionalWork && canDeleteAdditionalWork(userRole, item, currentUserId));

					return (
						<Box key={item.id ?? item.description} className={classes.card}>
							<Typography className={classes.cardTitle}>
								{item.description || "—"}
							</Typography>
							<div className="divider" />
							<Box className={classes.cardBody}>
								{hasBodyContent ? (
									<>
										{hasPrimaryLine && (
											<Typography component="div" className={classes.metaLine}>
												{hasQtyOrUnit && (
													<span className={classes.metaPrimary}>
														{[hasQty ? strTrim(item.quantity) : null, hasUnit ? strTrim(item.unit) : null]
															.filter(Boolean)
															.join(" ")}
													</span>
												)}
												{hasQtyOrUnit && hasCost && (
													<span className={classes.metaPrimary}> · </span>
												)}
												{hasCost && (
													<span className={classes.metaAccent}>{formatCostRub(item.cost)}</span>
												)}
											</Typography>
										)}
										{hasMechanic && (
											<Typography className={classes.mechanicLine}>
												Механик: {mechanicName}
											</Typography>
										)}
										{Boolean(createdLabel) && (
											<Typography className={classes.dateLine}>{createdLabel}</Typography>
										)}
									</>
								) : !hasPictures ? (
									<Typography className={classes.metaPrimary}>—</Typography>
								) : null}
								{hasPictures && (
									<Box
										className={`${classes.picturesRow}${hasBodyContent ? ` ${classes.picturesRowSpaced}` : ""}`}
									>
										{pictureItems.map(({pic, src}, index) => (
											<Box
												key={pic.id ?? src}
												component="img"
												src={src}
												alt=""
												className={`${classes.pictureThumb}${
													onOpenDialogImagesVideos ? ` ${classes.pictureThumbInteractive}` : ""
												}`}
												onClick={
													onOpenDialogImagesVideos
														? () => onOpenDialogImagesVideos(galleryFiles, index)
														: undefined
												}
											/>
										))}
									</Box>
								)}
							</Box>
							{(canEdit || canDelete) && (
								<>
									<div className="divider" />
									<Box className={classes.cardToolbar}>
										<Grid
											container
											spacing={1}
											wrap="nowrap"
											alignItems="center"
											justifyContent={
												canEdit && canDelete
													? "space-between"
													: canDelete && !canEdit
														? "flex-end"
														: "flex-start"
											}
										>
											{canEdit && (
												<Grid item>
													<Button
														variant="contained"
														onClick={() => onEditAdditionalWork(item)}
													>
														Редактировать
													</Button>
												</Grid>
											)}
											{canDelete && (
												<Grid item>
													<Button
														variant="outlined"
														color="error"
														onClick={() => onDeleteAdditionalWork(item.id, false)}
													>
														Удалить
													</Button>
												</Grid>
											)}
										</Grid>
									</Box>
								</>
							)}
						</Box>
					);
				})}
			</Box>
		</>
	);
};

const useStyles = makeStyles((theme) => ({
	sectionHeader: {
		marginBottom: 16,
	},
	sectionTitle: {
		fontWeight: 700,
		color: "#231F20",
	},
	headerButton: {
		borderRadius: 4,
		textTransform: "none",
		fontWeight: 600,
		padding: "6px 14px",
		boxShadow: "none",
		backgroundColor: theme.palette?.primary?.main ?? "#d32f2f",
		"&:hover": {
			boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
		},
	},

	list: {
		display: "flex",
		flexDirection: "column",
		gap: 12,
	},
	// Совпадает с WorkList: workCard
	card: {
		padding: "12px 14px",
		background: "white",
		borderRadius: "16px",
		boxSizing: "border-box",
		border: "1px solid #1524351A",

		"& .divider": {
			margin: "10px -14px",
			height: "1px",
			backgroundColor: "#1524351A",
		},
	},
	cardTitle: {
		fontSize: "16px",
		color: "#231F20",
		lineHeight: "18px",
		fontWeight: "600",
	},
	cardToolbar: {
		width: "100%",
		paddingTop: 2,
		"& .MuiButton-root": {
			textTransform: "none",
			fontWeight: 600,
		},
	},
	cardBody: {
		margin: 0,
	},
	metaLine: {
		fontSize: "14px",
		lineHeight: "16px",
		margin: 0,
		letterSpacing: "-0.02em",
	},
	// как workCardNameWork
	metaPrimary: {
		color: "#808083",
	},
	// как workCardEstimate
	metaAccent: {
		fontSize: 12,
		fontWeight: 500,
		color: "#006FBA",
	},
	// как workCardTotalTime
	mechanicLine: {
		marginTop: 8,
		fontSize: "13px",
		lineHeight: "14px",
		color: "#006FBA",
	},
	dateLine: {
		marginTop: 6,
		fontSize: 12,
		lineHeight: "14px",
		color: "#808083",
	},
	picturesRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 8,
		alignItems: "center",
	},
	picturesRowSpaced: {
		marginTop: 10,
	},
	pictureThumb: {
		width: 72,
		height: 72,
		objectFit: "cover",
		borderRadius: 8,
		border: "1px solid #1524351A",
		display: "block",
	},
	pictureThumbInteractive: {
		cursor: "pointer",
		transition: "opacity 0.15s ease",
		"&:hover": {
			opacity: 0.88,
		},
	},
}));

export default AdditionalWorksList;
